import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

const DEFAULT_LANGUAGE = 'en';

/**
 * Single-language context (English only).
 *
 * The site used to fetch available languages from the DB and redirect the URL
 * into a /:lang prefix. That is gone: text is hardcoded English, URLs have no
 * language prefix, and this context only exists so the ~40 consuming
 * components keep their API. Prefix helpers are identity functions.
 */
export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();

  // Keep react-i18next in sync (SEO and legacy code read i18n.language directly)
  useEffect(() => {
    i18n.changeLanguage(DEFAULT_LANGUAGE);
  }, [i18n]);

  const language = DEFAULT_LANGUAGE;
  const availableLanguages = [DEFAULT_LANGUAGE];

  const changeLanguage = () => {
    // No-op: the public site is English-only
  };

  const getLanguageFromPath = () => null;

  const removeLanguagePrefix = (pathname) => pathname;

  const addLanguagePrefix = (pathname) => pathname;

  const value = useMemo(() => ({
    language,
    currentLang: language,
    changeLanguage,
    validLanguages: availableLanguages,
    availableLanguages,
    languagesLoading: false,
    isInitialized: true,
    getLanguageFromPath,
    removeLanguagePrefix,
    addLanguagePrefix,
  }), []);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
